import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import TooltipButton from 'tio-ui/components/tooltip-button';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import {
  PhCreditCard,
  PhFile,
  PhCalendarDots,
  PhChatCenteredText,
  PhTrash,
} from 'ember-phosphor-icons';
import { reloadRecord } from 'tio-data/requests/save-record';
import {
  abandonCourseRequest,
  buildTasTransitionFieldData,
} from 'tio-data/requests/tas-application-actions';
import { abandonProgramRequest } from 'tio-data/requests/tas-program-instance';
import { buildTasEmployeeActor } from 'tio-data/utils/tas-actor';
import type { IntlService } from 'ember-intl';
import type RouterService from '@ember/routing/router-service';
import type { Store as StoreService } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';

export type ActionToolbarDrawerType = 'attachments' | 'timeline' | 'comments' | 'course-cash';

interface ActionToolbarSignature {
  Element: HTMLDivElement;
  Args: {
    application: TasApplicationModel;
    activeDrawer?: string | null;
    onDrawerOpen: (drawerType: ActionToolbarDrawerType) => void;
    /** Employee-visible comments added since the drawer was last opened. Owned by
     * ManagePage (which holds the read timestamp) so this stays presentational. */
    unreadCommentCount?: number;
  };
}

/**
 * Bottom action toolbar for the reimagined TAS "manage" screen (employee
 * course-management). Deferred out of the original reimagined-tas-course-
 * management-screen plan; built now per Figma "Sticky Footer". Pattern-matched
 * against the admin equivalent, `tas/approval/approval-footer.gts`.
 */
export default class ActionToolbar extends Component<ActionToolbarSignature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;

  @tracked isWithdrawModalOpen = false;
  @tracked withdrawError: string | null = null;

  get hasUnreadComments(): boolean {
    return (this.args.unreadCommentCount ?? 0) > 0;
  }

  /** Capped so a long-running thread can't blow out the 20px icon button. */
  get unreadCommentLabel(): string {
    const count = this.args.unreadCommentCount ?? 0;
    return count > 9 ? '9+' : String(count);
  }

  /**
   * Whether the CourseCash icon should show — mirrors attention-tasks.ts's
   * own CourseCash gating (`reserved > 0`, i.e. a card has actually been
   * issued/provisioned), not just `paymentMethod`, which doesn't capture
   * provisioning on its own.
   */
  get showCourseCash(): boolean {
    const amounts = this.args.application.amounts as
      { course_cash?: { reserved?: number } | null } | null | undefined;
    return (amounts?.course_cash?.reserved ?? 0) > 0;
  }

  get typeClassification(): string | undefined {
    return this.args.application.tasProgramInstance?.tasProgramTemplate?.typeClassification;
  }

  get isPaid(): boolean {
    return this.args.application.isPaid;
  }

  get courseCashUsed(): boolean {
    const amounts = this.args.application.amounts as
      { course_cash?: { used?: number } | null } | null | undefined;
    return (amounts?.course_cash?.used ?? 0) > 0;
  }

  /**
   * Mirrors the legacy show.gts page's `showTrashIcon` gate exactly —
   * withdrawal is unavailable once the request has been paid or CourseCash
   * has been used, in a handful of terminal states, and (for Type 4.2/4.3
   * specifically) unless the parent program instance is still PRIMED.
   */
  get canWithdraw(): boolean {
    if (this.isPaid || this.courseCashUsed) {
      return false;
    }
    const statesNotAllowed = new Set([
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.ABANDONED',
      'TAS.ApplicationState.COURSES_REJECTED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
      'TAS.ApplicationState.REJECTED_PER_POLICY',
    ]);
    if (statesNotAllowed.has(this.args.application.state)) {
      return false;
    }
    if (
      this.typeClassification === 'TAS.ProgramType.4.2' ||
      this.typeClassification === 'TAS.ProgramType.4.3'
    ) {
      return this.args.application.tasProgramInstance?.state === 'TAS.ProgramInstanceState.PRIMED';
    }
    return true;
  }

  /**
   * Type 4 abandons only the application (type-four.gts's deleteApplication);
   * Types 2/3 also abandon the parent program instance
   * (type-two-or-three.gts's deleteApplication). This toolbar renders on all
   * three guided groups' manage routes, so it must branch the same way —
   * unconditionally abandoning the program instance would incorrectly take
   * down the whole Type 4 program over a single application's withdrawal.
   */
  get isType4(): boolean {
    return this.typeClassification?.startsWith('TAS.ProgramType.4') ?? false;
  }

  /**
   * Composes the localized "CourseCa$h™ Card" tooltip with the canonical
   * brand mark (`common.tas.course_cash.default`) injected as a SafeString —
   * see approval-footer.gts's identical `courseCashTooltip` getter. The mark
   * is real HTML (a styled "$" + trademark superscript), so it must always be
   * interpolated with `htmlSafe: true`, never written as a plain-text literal.
   */
  get courseCashTooltip() {
    return this.intl.t('tuition_assistance.applications.manage.action_toolbar.course_cash', {
      htmlSafe: true,
      courseCash: this.intl.t('common.tas.course_cash.default', { htmlSafe: true }),
    });
  }

  isDrawerActive = (drawerType: ActionToolbarDrawerType): boolean => {
    return this.args.activeDrawer === drawerType;
  };

  handleDrawerClick = (drawerType: ActionToolbarDrawerType) => {
    this.args.onDrawerOpen(drawerType);
  };

  openWithdrawModal = () => {
    this.withdrawError = null;
    this.isWithdrawModalOpen = true;
  };

  closeWithdrawModal = () => {
    this.isWithdrawModalOpen = false;
    this.withdrawError = null;
  };

  /**
   * Withdraw the request. Mirrors the guided wizard's own `deleteApplication`
   * task: abandon the application, reload (the `abandon` transition renders
   * no resource), then — for Types 2/3 only, matching
   * type-two-or-three.gts's deleteApplication — abandon the parent program
   * instance too. Type 4 (type-four.gts's deleteApplication) abandons only
   * the application. Then leave the screen — there is nothing left here to
   * manage once withdrawn.
   */
  withdrawTask = dropTask(async () => {
    this.withdrawError = null;
    try {
      const application = this.args.application;
      const actor = buildTasEmployeeActor(
        this.sessionContext.user,
        this.sessionContext.currentRole
      );

      await this.store.request(
        abandonCourseRequest(
          application.id as string,
          actor,
          '',
          buildTasTransitionFieldData(application.fields)
        )
      );
      await reloadRecord(this.store, application);

      if (!this.isType4) {
        await this.store.request(
          abandonProgramRequest(application.tasProgramInstance.id as string, actor)
        );
      }

      this.closeWithdrawModal();
      this.router.transitionTo('authenticated.tas.dashboard');
    } catch (e) {
      console.error('Error withdrawing application:', e);
      this.withdrawError = e instanceof Error ? e.message : String(e);
    }
  });

  <template>
    <div
      class="-mx-6 -mb-6 tio-bg-surface border-t tio-border-default tio-row-extra-tight justify-end p-2"
      data-test-manage-action-toolbar
      ...attributes
    >
      {{#if this.showCourseCash}}
        <TooltipButton
          @tooltip={{this.courseCashTooltip}}
          @tooltipPlacement="top"
          @appearance="minimal"
          @intent="default"
          @size="sm"
          class="!p-2 !rounded-md"
          @isActive={{this.isDrawerActive "course-cash"}}
          data-test-button="manage-toolbar-course-cash-button"
          {{on "click" (fn this.handleDrawerClick "course-cash")}}
        >
          <PhCreditCard @size="20" />
        </TooltipButton>
      {{/if}}

      <TooltipButton
        @tooltip={{t "tuition_assistance.applications.manage.action_toolbar.attachments"}}
        @tooltipPlacement="top"
        @appearance="minimal"
        @intent="default"
        @size="sm"
        class="!p-2 !rounded-md"
        @isActive={{this.isDrawerActive "attachments"}}
        data-test-button="manage-toolbar-attachments-button"
        {{on "click" (fn this.handleDrawerClick "attachments")}}
      >
        <PhFile @size="20" />
      </TooltipButton>

      <TooltipButton
        @tooltip={{t "tuition_assistance.applications.manage.action_toolbar.timeline"}}
        @tooltipPlacement="top"
        @appearance="minimal"
        @intent="default"
        @size="sm"
        class="!p-2 !rounded-md"
        @isActive={{this.isDrawerActive "timeline"}}
        data-test-button="manage-toolbar-timeline-button"
        {{on "click" (fn this.handleDrawerClick "timeline")}}
      >
        <PhCalendarDots @size="20" />
      </TooltipButton>

      {{! The unread count goes in the TOOLTIP, and the visual badge is
          aria-hidden — the tooltip is this button's accessible name, so putting
          the number in both would announce it twice. }}
      <TooltipButton
        @tooltip={{if
          this.hasUnreadComments
          (t
            "tuition_assistance.applications.manage.action_toolbar.comments_unread"
            count=@unreadCommentCount
          )
          (t "tuition_assistance.applications.manage.action_toolbar.comments")
        }}
        @tooltipPlacement="top"
        @appearance="minimal"
        @intent="default"
        @size="sm"
        class="!p-2 !rounded-md relative"
        @isActive={{this.isDrawerActive "comments"}}
        data-test-button="manage-toolbar-comments-button"
        {{on "click" (fn this.handleDrawerClick "comments")}}
      >
        <PhChatCenteredText @size="20" />
        {{#if this.hasUnreadComments}}
          {{! Notification-badge geometry, per the macOS reference: a fixed circle
              pinned to the button's top-right corner, with a 2px ring in the
              SURFACE colour so the badge is punched out of whatever sits behind
              it instead of smudging into the icon. `tio-border-surface` rather
              than a literal white keeps that working in dark mode.

              The button's own `!p-2` means -top-1/-right-1 lands the badge mostly
              in the padding, so it only nicks the icon's corner. size-5 with a
              2px ring leaves a 16px interior — enough for "9+" at text-xs, so the
              badge stays a true circle at every count instead of stretching. }}
          <span
            class="absolute -top-1 -right-1 tio-row-flush size-5 justify-center rounded-full border-2 tio-border-surface tio-bg-danger tio-fg-inverse tio-text-xs-semibold leading-none tabular-nums"
            aria-hidden="true"
            data-test-unread-comment-badge
          >
            {{this.unreadCommentLabel}}
          </span>
        {{/if}}
      </TooltipButton>

      {{#if this.canWithdraw}}
        <TooltipButton
          @tooltip={{t "tuition_assistance.applications.manage.action_toolbar.withdraw"}}
          @tooltipPlacement="top"
          @appearance="minimal"
          @intent="danger"
          @size="sm"
          class="!p-2 !rounded-md"
          data-test-button="manage-toolbar-withdraw-button"
          {{on "click" this.openWithdrawModal}}
        >
          <PhTrash @size="20" />
        </TooltipButton>
      {{/if}}
    </div>

    <Modal
      @isOpen={{this.isWithdrawModalOpen}}
      @onClose={{this.closeWithdrawModal}}
      data-test-withdraw-application-modal
    >
      <:header>{{t
          "tuition_assistance.applications.manage.action_toolbar.withdraw_modal_title"
        }}</:header>
      <:body>
        <p>{{t "tuition_assistance.applications.manage.action_toolbar.withdraw_modal_body"}}</p>
        {{#if this.withdrawError}}
          <p class="tio-fg-danger tio-text-sm-normal mt-2">{{this.withdrawError}}</p>
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.withdrawTask.isRunning}}
          @onCancel={{this.closeWithdrawModal}}
          @onSubmit={{this.withdrawTask.perform}}
        />
      </:footer>
    </Modal>
  </template>
}
